import { BracketType, BracketTypeEnum } from "../Common/types";

/** Mirrors backend Panel\Brackets\Settings\KataSettings. */
export type KataSettings = {
    numberOfPerformances?: number;
};

/** Mirrors backend Panel\Brackets\Settings\RepechageByFinalistsSettings. */
export type RepechageByFinalistsSettings = {
    numberOfThirdPlaces?: number;
    pullDepth?: number | null;
};

// Per-bracket-type settings shape. Add a new member here (named after its backend
// Panel\Brackets\Settings\* counterpart) whenever a new bracket type gains settings.
export type BracketTypeSettings = KataSettings | RepechageByFinalistsSettings;

export type BracketTypeConstraint = {
    lowerLimit: number;
    upperLimit: number;
    bracketType: BracketType;
    bracketTypeSettings?: BracketTypeSettings | null; //TODO Should not be optional.
};

export function getIbjjfTemplate(): Array<BracketTypeConstraint> {
    return [
        {
            lowerLimit: 2,
            upperLimit: 2,
            bracketType: BracketTypeEnum.SingleElimination,
        },
        {
            lowerLimit: 3,
            upperLimit: 3,
            bracketType: BracketTypeEnum.ThreeCompetitorRepechage,
        },
        {
            lowerLimit: 4,
            upperLimit: 256,
            bracketType: BracketTypeEnum.SingleElimination,
        },
    ];
}
export function getWrestlingTemplate() {
    return [
        {
            lowerLimit: 2,
            upperLimit: 2,
            bracketType: BracketTypeEnum.SingleElimination,
        },
        {
            lowerLimit: 3,
            upperLimit: 5,
            bracketType: BracketTypeEnum.RoundRobin,
        },
        {
            lowerLimit: 6,
            upperLimit: 7,
            bracketType: BracketTypeEnum.TwoPoolsTwo3rdPlaces,
        },
        {
            lowerLimit: 8,
            upperLimit: 32,
            bracketType: BracketTypeEnum.DoubleEliminationTwo3rdPlaces,
        },
    ];
}
export function getSubmissionFightingTemplate() {
    return [
        {
            lowerLimit: 2,
            upperLimit: 3,
            bracketType: BracketTypeEnum.SingleElimination,
        },
        {
            lowerLimit: 4,
            upperLimit: 256,
            bracketType: BracketTypeEnum.SingleElimination3rdPlacePlayoff,
        },
    ];
}
// SE throughout — used by striking sports and ADCC Worlds
const singleEliminationPreset: Array<BracketTypeConstraint> = [
    { lowerLimit: 2, upperLimit: 256, bracketType: BracketTypeEnum.SingleElimination },
];

// SE for small groups, SE+3rd for larger — standard local striking sport format
const strikingStandardPreset: Array<BracketTypeConstraint> = [
    { lowerLimit: 2, upperLimit: 7, bracketType: BracketTypeEnum.SingleElimination },
    { lowerLimit: 8, upperLimit: 256, bracketType: BracketTypeEnum.SingleElimination3rdPlacePlayoff },
];

// RR for small, TwoPools for medium, SE3rd for large — IJF/UWW/FIAS grappling standard
const ijfStylePreset: Array<BracketTypeConstraint> = [
    { lowerLimit: 2, upperLimit: 2, bracketType: BracketTypeEnum.BestOfThree },
    { lowerLimit: 3, upperLimit: 5, bracketType: BracketTypeEnum.RoundRobin },
    { lowerLimit: 6, upperLimit: 7, bracketType: BracketTypeEnum.TwoPoolsTwo3rdPlaces },
    { lowerLimit: 8, upperLimit: 256, bracketType: BracketTypeEnum.RepechageByFinalists },
];

type DisciplinePreset = { name: string; constraints: BracketTypeConstraint[] };

const DISCIPLINE_PRESETS: Record<string, DisciplinePreset[]> = {
    bjj: [
        { name: "IBJJF", constraints: getIbjjfTemplate() },
        { name: "Simple", constraints: singleEliminationPreset },
    ],
    submissionFighting: [
        { name: "Standard (ADCC local)", constraints: getSubmissionFightingTemplate() },
        { name: "ADCC Worlds", constraints: singleEliminationPreset },
    ],
    wrestling: [{ name: "UWW Standard", constraints: getWrestlingTemplate() }],
    grappling: [{ name: "UWW Standard", constraints: getWrestlingTemplate() }],
    sambo: [
        { name: "Sport Sambo (FIAS)", constraints: ijfStylePreset },
        { name: "Combat Sambo (FIAS)", constraints: singleEliminationPreset },
    ],
    judo: [{ name: "IJF Standard", constraints: ijfStylePreset }],
    mma: [{ name: "Standard", constraints: strikingStandardPreset }],
    combatJuJutsu: [
        {
            name: "Standard",
            constraints: [
                { lowerLimit: 2, upperLimit: 3, bracketType: BracketTypeEnum.SingleElimination },
                { lowerLimit: 4, upperLimit: 256, bracketType: BracketTypeEnum.SingleElimination3rdPlacePlayoff },
            ],
        },
    ],
    kickboxing: [{ name: "WAKO Standard", constraints: strikingStandardPreset }],
    boxing: [{ name: "IBA Standard", constraints: strikingStandardPreset }],
    muayThai: [{ name: "IFMA Standard", constraints: strikingStandardPreset }],
    taekwondo: [
        {
            name: "WT Standard",
            constraints: [
                { lowerLimit: 2, upperLimit: 5, bracketType: BracketTypeEnum.RoundRobin },
                { lowerLimit: 6, upperLimit: 256, bracketType: BracketTypeEnum.SingleElimination3rdPlacePlayoff },
            ],
        },
    ],
    karate: [
        {
            name: "WKF Standard",
            constraints: [
                { lowerLimit: 2, upperLimit: 5, bracketType: BracketTypeEnum.RoundRobin },
                { lowerLimit: 6, upperLimit: 7, bracketType: BracketTypeEnum.TwoPoolsTwo3rdPlaces },
                { lowerLimit: 8, upperLimit: 256, bracketType: BracketTypeEnum.SingleElimination3rdPlacePlayoff },
            ],
        },
    ],
    aikido: [{ name: "Standard", constraints: ijfStylePreset }],
    juJitsu: [
        {
            name: "Standard",
            constraints: [
                { lowerLimit: 2, upperLimit: 2, bracketType: BracketTypeEnum.BestOfThree },
                { lowerLimit: 3, upperLimit: 5, bracketType: BracketTypeEnum.RoundRobin },
                { lowerLimit: 6, upperLimit: 6, bracketType: BracketTypeEnum.TwoPoolsTwo3rdPlaces },
                { lowerLimit: 7, upperLimit: 32, bracketType: BracketTypeEnum.DoubleEliminationTwo3rdPlaces },
                {
                    lowerLimit: 33,
                    upperLimit: 256,
                    bracketType: BracketTypeEnum.RepechageByFinalists,
                    bracketTypeSettings: { numberOfThirdPlaces: 2 },
                },
            ],
        },
    ],
    kata: [
        {
            name: "1 performance (all)",
            constraints: [
                {
                    lowerLimit: 2,
                    upperLimit: 256,
                    bracketType: BracketTypeEnum.Kata,
                    bracketTypeSettings: { numberOfPerformances: 1 },
                },
            ],
        },
        {
            name: "2 performances (all)",
            constraints: [
                {
                    lowerLimit: 2,
                    upperLimit: 256,
                    bracketType: BracketTypeEnum.Kata,
                    bracketTypeSettings: { numberOfPerformances: 2 },
                },
            ],
        },
    ],
};

export function getPresetsForDiscipline(discipline: string): DisciplinePreset[] {
    return DISCIPLINE_PRESETS[discipline] ?? [];
}
//# sourceMappingURL=configurationTemplates.js.map
